// Item 70 Phase F — Admin Observability page.
//
// Read-only triage view that surfaces the most-watched live numbers
// emitted by the API server's Prometheus registry: HTTP RED, pg-pool
// USE, S3 latency / error rate, scan-queue depth, retention sweep,
// replication lag per peer, and license expiry. The auto-refresh
// cadence (10s) matches the Grafana-default scrape interval so what an
// operator sees here lines up with their dashboards.
//
// This page is a starting point for triage — links out to /metrics
// (the raw scrape), the bundled Grafana dashboards under docs/grafana/,
// the alerts file under docs/prometheus/, and the monitoring runbook
// in docs-site so an operator can pivot from "something looks off"
// to "let me drop into Grafana / check the runbook" in one click.

import { useState } from 'react';
import {
  Activity,
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  Database,
  Download,
  ExternalLink,
  FileCode2,
  HardDrive,
  Layers,
  RefreshCw,
  ShieldCheck,
  Timer,
  Trash2,
} from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { useObservabilitySnapshot } from '../../api/observability';

// Shape one number for display. Counters can grow large, so >10k flips
// to a thousands abbreviation; smaller values stay precise.
function fmt(n: number, opts?: { decimals?: number; suffix?: string }): string {
  if (!Number.isFinite(n)) return '—';
  const decimals = opts?.decimals ?? 0;
  const suffix = opts?.suffix ?? '';
  let body: string;
  if (Math.abs(n) >= 1_000_000) {
    body = (n / 1_000_000).toFixed(1) + 'M';
  } else if (Math.abs(n) >= 10_000) {
    body = (n / 1_000).toFixed(1) + 'k';
  } else if (decimals > 0 || Math.abs(n) < 10) {
    body = n.toFixed(decimals);
  } else {
    body = Math.round(n).toLocaleString();
  }
  return body + suffix;
}

function fmtSeconds(s: number): string {
  if (!Number.isFinite(s) || s < 0) return '—';
  if (s < 1) return `${(s * 1000).toFixed(0)} ms`;
  if (s < 60) return `${s.toFixed(1)} s`;
  const m = Math.floor(s / 60);
  const rest = Math.floor(s % 60);
  if (m < 60) return `${m}m ${rest}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

function fmtPercent(ratio: number): string {
  if (!Number.isFinite(ratio)) return '—';
  return (ratio * 100).toFixed(1) + '%';
}

// Latency average from Prometheus histogram _sum / _count. Returns
// seconds, or null when no samples have been recorded yet.
function avgLatency(sum: number, count: number): number | null {
  if (count <= 0) return null;
  return sum / count;
}

// Tone the pg-pool saturation badge: above 80% paints amber, above 95%
// paints red. Mirrors the OrbitalRegPgPoolNearExhausted alert that
// fires at 80% in the bundled rules file.
function poolTone(sat: number): 'success' | 'warning' | 'danger' {
  if (sat >= 0.95) return 'danger';
  if (sat >= 0.8) return 'warning';
  return 'success';
}

function licenseTone(expiresIn: number): 'success' | 'warning' | 'danger' {
  if (!Number.isFinite(expiresIn) || expiresIn === 0) return 'success';
  if (expiresIn < 0) return 'danger';
  if (expiresIn < 30 * 86_400) return 'warning';
  return 'success';
}

function licenseExpiryText(expiresInSeconds: number): string {
  if (expiresInSeconds === 0) return '—';
  if (expiresInSeconds < 0) return `expired (${fmtSeconds(-expiresInSeconds)} ago)`;
  return fmtSeconds(expiresInSeconds);
}

function peerLagIndicator(lagSeconds: number) {
  if (lagSeconds === 0) {
    return (
      <span className="text-success inline-flex items-center gap-1.5">
        <CheckCircle2 className="h-3.5 w-3.5" />
        caught up
      </span>
    );
  }
  if (lagSeconds > 60) {
    return (
      <span className="text-warning inline-flex items-center gap-1.5">
        <AlertTriangle className="h-3.5 w-3.5" />
        {fmtSeconds(lagSeconds)}
      </span>
    );
  }
  return <span className="text-fg-muted">{fmtSeconds(lagSeconds)}</span>;
}

interface MetricRowProps {
  readonly label: string;
  readonly value: string;
  readonly hint?: string;
  readonly tone?: 'neutral' | 'success' | 'warning' | 'danger';
}

function toneTextClass(tone: 'neutral' | 'success' | 'warning' | 'danger'): string {
  if (tone === 'danger') return 'text-danger';
  if (tone === 'warning') return 'text-warning';
  if (tone === 'success') return 'text-success';
  return 'text-fg';
}

function MetricRow({ label, value, hint, tone = 'neutral' }: MetricRowProps) {
  const valClass = toneTextClass(tone);
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/40 last:border-0 px-5 py-2.5">
      <span className="text-sm text-fg-muted">{label}</span>
      <span className="text-right">
        <span className={`font-mono text-sm font-medium ${valClass}`}>{value}</span>
        {hint && <span className="block text-xs text-fg-subtle mt-0.5">{hint}</span>}
      </span>
    </div>
  );
}

export function AdminObservability() {
  const { data, error, isLoading, mutate } = useObservabilitySnapshot();
  const [refreshing, setRefreshing] = useState(false);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await mutate();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <AdminShell
      title="Observability"
      description="Live snapshot of the most-watched RED + USE metrics. Auto-refreshes every 10 seconds. Drop into Grafana or the monitoring runbook for deeper triage."
    >
      <Card>
        <CardHeader
          title="Snapshot"
          description={
            data
              ? `Generated ${new Date(data.generated_at).toLocaleTimeString()} · uptime ${fmtSeconds(data.uptime_seconds)}`
              : 'Loading…'
          }
          actions={
            <div className="flex items-center gap-2">
              {data && (
                <Badge tone="neutral" title={`Built ${data.build.built_at || 'unknown'}`}>
                  v{data.build.version}
                </Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={refresh}
                disabled={refreshing || isLoading}
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`}
                />
                Refresh
              </Button>
            </div>
          }
        />
        <CardBody>
          {error ? (
            <p className="text-danger font-mono text-sm">{String(error)}</p>
          ) : (
            <p className="text-sm text-fg-muted">
              Numbers come straight from the API server's Prometheus registry —
              the same series your scrape sees. Counters are cumulative since
              process start; gauges are point-in-time. Open the raw scrape via{' '}
              <a
                href="/metrics"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:underline"
              >
                /metrics
              </a>{' '}
              for everything else.
            </p>
          )}
        </CardBody>
      </Card>

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader
              title={
                <span className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-accent" />
                  HTTP (RED)
                </span>
              }
              description="Requests rate, errors, latency average."
            />
            <div>
              <MetricRow label="Requests total" value={fmt(data.http.requests_total)} />
              <MetricRow
                label="5xx total"
                value={fmt(data.http.requests_5xx_total)}
                tone={data.http.requests_5xx_total > 0 ? 'warning' : 'neutral'}
              />
              <MetricRow label="4xx total" value={fmt(data.http.requests_4xx_total)} />
              <MetricRow label="Inflight" value={fmt(data.http.inflight)} />
              <MetricRow
                label="Avg latency"
                value={
                  avgLatency(data.http.duration_sum_seconds, data.http.duration_count) === null
                    ? '—'
                    : fmtSeconds(
                        avgLatency(data.http.duration_sum_seconds, data.http.duration_count) ?? 0,
                      )
                }
                hint={`${fmt(data.http.duration_count)} samples`}
              />
            </div>
          </Card>

          <Card>
            <CardHeader
              title={
                <span className="flex items-center gap-2">
                  <Database className="h-4 w-4 text-accent" />
                  Postgres pool (USE)
                </span>
              }
              description="pgxpool.Stat() resampled every 10s."
              actions={
                <Badge tone={poolTone(data.pg_pool.saturation)}>
                  {fmtPercent(data.pg_pool.saturation)}
                </Badge>
              }
            />
            <div>
              <MetricRow label="Acquired" value={fmt(data.pg_pool.acquired)} />
              <MetricRow label="Idle" value={fmt(data.pg_pool.idle)} />
              <MetricRow label="Total" value={fmt(data.pg_pool.total)} />
              <MetricRow label="Max" value={fmt(data.pg_pool.max)} />
              <MetricRow
                label="Saturation"
                value={fmtPercent(data.pg_pool.saturation)}
                tone={poolTone(data.pg_pool.saturation)}
                hint="Acquired ÷ Max — alert fires at 80%."
              />
            </div>
          </Card>

          <Card>
            <CardHeader
              title={
                <span className="flex items-center gap-2">
                  <HardDrive className="h-4 w-4 text-accent" />
                  S3 / object storage
                </span>
              }
              description="Aggregate of all ops (put/get/stat/delete/list)."
            />
            <div>
              <MetricRow label="Requests total" value={fmt(data.s3.requests_total)} />
              <MetricRow
                label="Errors total"
                value={fmt(data.s3.errors_total)}
                tone={data.s3.errors_total > 0 ? 'warning' : 'neutral'}
              />
              <MetricRow
                label="Avg latency"
                value={
                  avgLatency(data.s3.duration_sum_seconds, data.s3.duration_count) === null
                    ? '—'
                    : fmtSeconds(
                        avgLatency(data.s3.duration_sum_seconds, data.s3.duration_count) ?? 0,
                      )
                }
                hint={`${fmt(data.s3.duration_count)} samples`}
              />
              <MetricRow
                label="Error rate"
                value={
                  data.s3.requests_total > 0
                    ? fmtPercent(data.s3.errors_total / data.s3.requests_total)
                    : '—'
                }
              />
            </div>
          </Card>

          <Card>
            <CardHeader
              title={
                <span className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-accent" />
                  Scan dispatcher
                </span>
              }
              description="Queue depth resampled every 15s."
              actions={
                data.scan.queue_pending + data.scan.queue_claimed + data.scan.queue_running >
                1000 ? (
                  <Badge tone="warning">backlog</Badge>
                ) : null
              }
            />
            <div>
              <MetricRow label="Pending" value={fmt(data.scan.queue_pending)} />
              <MetricRow label="Claimed" value={fmt(data.scan.queue_claimed)} />
              <MetricRow label="Running" value={fmt(data.scan.queue_running)} />
              <MetricRow
                label="Failed (queued)"
                value={fmt(data.scan.queue_failed)}
                tone={data.scan.queue_failed > 0 ? 'warning' : 'neutral'}
              />
              <MetricRow label="Processed total" value={fmt(data.scan.processed_total)} />
              <MetricRow
                label="Failed total"
                value={fmt(data.scan.failed_total)}
                tone={data.scan.failed_total > 0 ? 'warning' : 'neutral'}
              />
            </div>
          </Card>

          <Card>
            <CardHeader
              title={
                <span className="flex items-center gap-2">
                  <Trash2 className="h-4 w-4 text-accent" />
                  Retention sweeper
                </span>
              }
              description="Per-cycle wall-clock + cumulative deletes."
            />
            <div>
              <MetricRow label="Sweeps run" value={fmt(data.retention.sweep_duration_count)} />
              <MetricRow
                label="Avg sweep duration"
                value={
                  avgLatency(
                    data.retention.sweep_duration_sum_seconds,
                    data.retention.sweep_duration_count,
                  ) === null
                    ? '—'
                    : fmtSeconds(
                        avgLatency(
                          data.retention.sweep_duration_sum_seconds,
                          data.retention.sweep_duration_count,
                        ) ?? 0,
                      )
                }
              />
              <MetricRow
                label="Artifacts deleted"
                value={fmt(data.retention.artifacts_deleted_total)}
              />
            </div>
          </Card>

          <Card>
            <CardHeader
              title={
                <span className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-accent" />
                  License
                </span>
              }
            />
            <div>
              <MetricRow label="Derivations" value={fmt(data.license.checks_total)} />
              <MetricRow
                label="Expires in"
                value={licenseExpiryText(data.license.expires_in_seconds)}
                tone={licenseTone(data.license.expires_in_seconds)}
                hint="Set to 0 when no license is configured."
              />
            </div>
          </Card>
        </div>
      )}

      {data && data.replication.peers.length > 0 && (
        <Card>
          <CardHeader
            title={
              <span className="flex items-center gap-2">
                <Timer className="h-4 w-4 text-accent" />
                Replication peers
              </span>
            }
            description={`${data.replication.peers.length} peer(s) — lag, outbox depth, push errors.`}
          />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="px-5 py-3 font-medium text-fg-muted">Peer</th>
                  <th className="px-5 py-3 font-medium text-fg-muted">Lag</th>
                  <th className="px-5 py-3 font-medium text-fg-muted">Outbox depth</th>
                  <th className="px-5 py-3 font-medium text-fg-muted">Push errors</th>
                </tr>
              </thead>
              <tbody>
                {data.replication.peers.map((p) => (
                  <tr key={p.peer} className="border-b border-border/60 last:border-0">
                    <td className="px-5 py-3 font-mono text-fg">{p.peer}</td>
                    <td className="px-5 py-3 font-mono">
                      {peerLagIndicator(p.lag_seconds)}
                    </td>
                    <td className="px-5 py-3 font-mono text-fg-muted">{fmt(p.outbox_depth)}</td>
                    <td className="px-5 py-3 font-mono">
                      <span className={p.push_errors_total > 0 ? 'text-warning' : 'text-fg-muted'}>
                        {fmt(p.push_errors_total)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Triage helpers"
          description="Bundled assets every operator can pull into their own monitoring stack."
        />
        <CardBody>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2.5">
              <Download className="h-4 w-4 text-fg-muted shrink-0 mt-0.5" />
              <span>
                <a
                  href="/metrics"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline inline-flex items-center gap-1"
                >
                  Raw /metrics scrape
                  <ExternalLink className="h-3 w-3" />
                </a>{' '}
                — the full Prometheus text-format dump. Bound to the same canonical
                series declared in <code className="font-mono text-xs">api/internal/metrics/</code>.
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <Activity className="h-4 w-4 text-fg-muted shrink-0 mt-0.5" />
              <span>
                <a
                  href="/healthz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline inline-flex items-center gap-1"
                >
                  /healthz <ExternalLink className="h-3 w-3" />
                </a>{' '}
                ·{' '}
                <a
                  href="/readyz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline inline-flex items-center gap-1"
                >
                  /readyz <ExternalLink className="h-3 w-3" />
                </a>{' '}
                — kubelet probe endpoints. Liveness is unconditional; readiness
                gates on Postgres + the license-state derivation.
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <FileCode2 className="h-4 w-4 text-fg-muted shrink-0 mt-0.5" />
              <span>
                <strong>Grafana dashboards</strong> live under{' '}
                <code className="font-mono text-xs">docs/grafana/</code> in the source
                tree —{' '}
                <code className="font-mono text-xs">orbitalreg-overview.json</code> (12
                panels) and{' '}
                <code className="font-mono text-xs">orbitalreg-deep-dive.json</code>{' '}
                (18 panels). Import via Settings → Dashboards → Import → JSON paste.
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <AlertTriangle className="h-4 w-4 text-fg-muted shrink-0 mt-0.5" />
              <span>
                <strong>Alert rules</strong> ship at{' '}
                <code className="font-mono text-xs">
                  docs/prometheus/orbitalreg.alerts.yaml
                </code>{' '}
                — ten on-call alerts (5xx burn-rate, p99 latency, pg-pool, S3
                error rate, scan-queue backlog, replication lag, license expiry,
                retention sweep stuck, scan jobs failing) plus seven recording
                rules.
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <BookOpen className="h-4 w-4 text-fg-muted shrink-0 mt-0.5" />
              <span>
                <strong>Monitoring runbook</strong> at{' '}
                <code className="font-mono text-xs">
                  docs-site/operations/monitoring.md
                </code>{' '}
                — per-subsystem metric catalogue, three Alertmanager routing
                examples (PagerDuty + Slack, Slack-only, email-only for
                air-gapped), and a section-per-alert response playbook.
              </span>
            </li>
          </ul>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Sample triage queries" />
        <CardBody>
          <p className="text-sm text-fg-muted mb-3">
            Drop these into your Prometheus / Grafana Explore tab to answer the
            five most common triage questions. Each expression references only
            metrics that appear in this snapshot.
          </p>
          <ul className="space-y-3 text-sm font-mono">
            <li>
              <p className="text-xs text-fg-subtle font-sans mb-1">
                5xx error rate over the last 5 minutes
              </p>
              <code className="block bg-bg-inset rounded px-3 py-2 text-fg-muted overflow-x-auto whitespace-pre">
                {`sum(rate(http_requests_total{status=~"5.."}[5m]))
  / sum(rate(http_requests_total[5m]))`}
              </code>
            </li>
            <li>
              <p className="text-xs text-fg-subtle font-sans mb-1">
                p99 request latency, top 5 routes
              </p>
              <code className="block bg-bg-inset rounded px-3 py-2 text-fg-muted overflow-x-auto whitespace-pre">
                {`topk(5,
  histogram_quantile(0.99,
    sum by (route, le) (rate(http_request_duration_seconds_bucket[5m]))
  )
)`}
              </code>
            </li>
            <li>
              <p className="text-xs text-fg-subtle font-sans mb-1">
                pg-pool saturation
              </p>
              <code className="block bg-bg-inset rounded px-3 py-2 text-fg-muted overflow-x-auto whitespace-pre">
                {`pg_pool_acquired_connections / pg_pool_max_connections`}
              </code>
            </li>
            <li>
              <p className="text-xs text-fg-subtle font-sans mb-1">
                Scan-queue depth by state
              </p>
              <code className="block bg-bg-inset rounded px-3 py-2 text-fg-muted overflow-x-auto whitespace-pre">
                {`sum by (state) (scan_queue_depth)`}
              </code>
            </li>
            <li>
              <p className="text-xs text-fg-subtle font-sans mb-1">
                Replication lag per peer
              </p>
              <code className="block bg-bg-inset rounded px-3 py-2 text-fg-muted overflow-x-auto whitespace-pre">
                {`max by (peer) (replication_lag_seconds)`}
              </code>
            </li>
          </ul>
        </CardBody>
      </Card>
    </AdminShell>
  );
}
